import { Box, Button, Typography } from "@mui/material";
import MetricOverviewCard from "./MetricOverviewCard";
import SettingsIcon from "@mui/icons-material/Settings";
import { useState } from "react";
import WidgetsModal from "../WidgetsModal";
import { getDashboardColumnsWithoutDimensions } from "../../../components/Filter/FilterData/columnConfig";
import {
  useAmazonDashboardOverview,
  useAmazonDashboardOverviewChart,
  useDashboardOverview,
  useDashboardOverviewChart,
} from "../dashboardHooks";
import { getSpecificDataFormat } from "../../../components/Reports/Components/utils";
import { useGlobalContext } from "../../../context/global";
import {
  AMAZON_OVERVIEW_DEFAULT_VISIBLE,
  AMAZON_OVERVIEW_METRICS,
} from "./amazonOverviewConfig";

/**
 * @param {object} props
 * @param {boolean} [props.useAmazonOverview] — Amazon home dashboard: adset APIs
 * @param {string[]} [props.selectedPortfolios] — selected portfolio names
 * @param {boolean} [props.hideTrendGraph] — hide sparkline charts
 */
function OverviewCardSection({
  useAmazonOverview = false,
  selectedPortfolios = [],
  hideTrendGraph = false,
} = {}) {
  const dashboardColumnsWithoutDimensions =
    getDashboardColumnsWithoutDimensions() ?? [];

  const defaultVisible = useAmazonOverview
    ? AMAZON_OVERVIEW_DEFAULT_VISIBLE
    : ["spends", "clicks", "orders", "order_units", "revenue", "roas"];

  const [visibleOverviewCards, setVisibleOverviewCards] =
    useState(defaultVisible);

  const allAvailableMetricsConfig = (
    useAmazonOverview
      ? AMAZON_OVERVIEW_METRICS
      : dashboardColumnsWithoutDimensions
  ).map((column) => ({
    ...column,
    isVisible: visibleOverviewCards.includes(column.key),
  }));

  const amazonOverview = useAmazonDashboardOverview(
    selectedPortfolios,
    useAmazonOverview
  );
  const platformOverview = useDashboardOverview(!useAmazonOverview);

  const amazonChart = useAmazonDashboardOverviewChart(
    selectedPortfolios,
    useAmazonOverview && !hideTrendGraph
  );
  const platformChart = useDashboardOverviewChart(
    !useAmazonOverview && !hideTrendGraph
  );

  const { overviewData, loading } = useAmazonOverview
    ? amazonOverview
    : platformOverview;
  const { chartData: trendDataSeries, loading: trendDataSeriesLoading } =
    useAmazonOverview ? amazonChart : platformChart;

  const { calendarDate } = useGlobalContext();
  const [widgetsModalOpen, setWidgetsModalOpen] = useState(false);

  const handleOpenWidgetsModal = () => {
    setWidgetsModalOpen(true);
  };

  const handleCloseWidgetsModal = () => {
    setWidgetsModalOpen(false);
  };

  const handleApplyWidgets = (selectedWidgets) => {
    setVisibleOverviewCards(selectedWidgets);
  };

  const cardsLoading =
    loading || (hideTrendGraph ? false : trendDataSeriesLoading);

  return (
    <div>
      <Box sx={{ display: "flex", flexDirection: "column", my: 3.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            *Data shown from {getSpecificDataFormat(calendarDate[0].startDate)}{" "}
            to {getSpecificDataFormat(calendarDate[0].endDate)}
          </Typography>
          <Button
            variant="outlined"
            size="medium"
            onClick={handleOpenWidgetsModal}
            startIcon={<SettingsIcon fontSize="small" />}
            sx={{
              textTransform: "none",
              border: "none",
              fontWeight: 600,
              backgroundColor: "#F5F5F5",
              color: "#666",
              borderColor: "#E0E0E0",
              "&:hover": {
                backgroundColor: "#F5F5F5",
                borderColor: "#E0E0E0",
              },
            }}
          >
            Manage Widgets
          </Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
            gap: 2,
            mb: 1,
          }}
        >
          {allAvailableMetricsConfig
            ?.filter((metric) => metric.isVisible)
            ?.map((colConfig) => (
              <MetricOverviewCard
                key={colConfig?.key}
                title={colConfig?.shortTitle}
                columnConfig={colConfig}
                loading={cardsLoading}
                currentValue={overviewData?.current_data?.[colConfig?.key]}
                previousValue={overviewData?.comparison_data?.[colConfig?.key]}
                changeValue={
                  overviewData?.change_percentage_data?.[colConfig?.key]
                }
                trendDataSeries={
                  hideTrendGraph ? null : trendDataSeries?.[colConfig?.key]
                }
                hideTrendGraph={hideTrendGraph}
              />
            ))}
        </Box>
      </Box>
      <WidgetsModal
        open={widgetsModalOpen}
        onClose={handleCloseWidgetsModal}
        availableMetrics={allAvailableMetricsConfig}
        initialSelectedWidgets={visibleOverviewCards}
        onApply={handleApplyWidgets}
      />
    </div>
  );
}

export default OverviewCardSection;
